import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Chip,
  IconButton,
  Grid,
  Select,
  Card,
  MenuItem,
  useMediaQuery,
  LinearProgress,
  Pagination,
} from "@mui/material";
import {
  Search,
  EditOutlined,
  Visibility,
} from "@mui/icons-material";
import PreActivityDetailsView from "./PreActivityDetailsView";
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import { getAllPreSeasonActivitiesAction, getAllZonesAction, getAllRegionsAction, getAllAreasAction, getAllDistrictsAction, getAllSeasonsAction } from "../../store/actions";


export default function PreSeasonTargetManagement() {
  const navigate = useNavigate();
    // Helper to get season display name
    const getSeasonDisplayName = (activity) => {
      // Prefer activity.season if present
      if (activity.season && activity.season.name) {
        const name = activity.season.name;
        const year = activity.season.year || '';
        return year ? `${name} (${year})` : name;
      }
      // Fallback: lookup in Seasons array
      let seasonId = activity.season_id;
      const found = Seasons.find(s => s.seasonId === seasonId);
      if (found) {
        const name = found.name;
        const year = found.year || '';
        return year ? `${name} (${year})` : name;
      }
      return activity.season_id;
    };

  // Progress bar color based on percentage
  const getProgressBarColor = (percentage) => {
    if (percentage >= 100) return '#10B981'; // Green for 100%+
    if (percentage >= 75) return '#3B82F6'; // Blue for 75-99%
    if (percentage >= 50) return '#F59E0B'; // Orange for 50-74%
    return '#EF4444'; // Red for <50%
  };
  
  const isIpad = useMediaQuery('(min-width:768px) and (max-width:1024px)');

  const {successData:preSeasonActivities} = useSelector((state) => state.getAllPreSeasonActivitiesReducer);
  const {successData:zonesData} = useSelector((state) => state.getAllZonesReducer);
  const {successData:regionsData} = useSelector((state) => state.getAllRegionsReducer);
  const {successData:areasData} = useSelector((state) => state.getAllAreasReducer);
  const {successData:districtsData} = useSelector((state) => state.getAllDistrictsReducer);
  const {successData:seasonsData} = useSelector((state) => state.getAllSeasonsReducer);

  const [filters, setFilters] = useState({
    search: "",
    locationType: "",
    currentSeason: "",
    district: "",
    zone: "",
    region: "",
    area: "",
  });

  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [viewRow, setViewRow] = useState(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 30;

  const dispatch = useDispatch();
  useEffect(() => {
    // Build query params for filters
    const params = [];
    if (filters.currentSeason) params.push(`seasonId=${filters.currentSeason}`);
    if (filters.zone) params.push(`zoneId=${filters.zone}`);
    if (filters.region) params.push(`regionId=${filters.region}`);
    if (filters.area) params.push(`areaId=${filters.area}`);
    if (filters.district) params.push(`districtId=${filters.district}`);
    // Add more filters as needed
    const query = params.length ? `target/pre-season?${params.join('&')}` : 'target/pre-season';
    dispatch(getAllPreSeasonActivitiesAction({ endPoint: query }));
    dispatch(getAllDistrictsAction());
    dispatch(getAllSeasonsAction());
    dispatch(getAllZonesAction());
    dispatch(getAllRegionsAction());
    dispatch(getAllAreasAction());
  }, [dispatch, filters.currentSeason, filters.zone, filters.region, filters.area, filters.district]);

  const District = districtsData?.data?.districts || [];
  const Zones = zonesData?.data?.zones || [];
  const Regions = regionsData?.data?.regions || [];
  const Areas = areasData?.data?.areas || [];
  const Seasons = seasonsData?.data?.seasons || [];

  // Filter regions by selected zone
  const filteredRegions = React.useMemo(() => {
    if (!filters.zone) return Regions;
    return Regions.filter(region => String(region.zoneId) === String(filters.zone));
  }, [Regions, filters.zone]);

  // Filter areas by selected region
  const filteredAreas = React.useMemo(() => {
    if (!filters.region) return Areas;
    return Areas.filter(area => String(area.regionId) === String(filters.region));
  }, [Areas, filters.region]);

  // Helper to get area display name
  const getAreaDisplayName = (activity) => {
    if (!(activity?.target_area_type && activity?.target_area_id)) return '';
    const type = activity.target_area_type.toLowerCase();
    if (type === 'mandal') {
      if (activity.target_area?.name) {
        return activity.target_area.name;
      }
      return activity.target_area_id;
    }
    if (type === 'district') {
      const district = District.find(d => (d.districtId || d.district_id) === activity.target_area_id || d.district_id === activity.target_area_id);
      if (district) {
        return district.name || '';
      }
      if (activity.target_area?.name) {
        return activity.target_area.name;
      }
      return activity.target_area_id;
    }
    if (type === 'region') {
      if (activity.target_area?.region_name) {
        return activity.target_area.region_name;
      }
      if (activity.target_area?.name) {
        return activity.target_area.name;
      }
      return activity.target_area_id;
    }
    if (type === 'area') {
      if (activity.target_area?.area_name) {
        return activity.target_area.area_name;
      }
      if (activity.target_area?.name) {
        return activity.target_area.name;
      }
      return activity.target_area_id;
    }
    if (activity.target_area?.name) {
      return activity.target_area.name;
    }
    return activity.target_area_id;
  };

  // Filter the activities based on search and filter values
  // Always use preSeasonActivities?.data as the array, fallback to []
  const filteredActivities = React.useMemo(() => {
    let filtered = Array.isArray(preSeasonActivities?.data) ? preSeasonActivities.data : [];

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(activity => {
        const seasonName = String(getSeasonDisplayName(activity) || '').toLowerCase();
        const areaName = String(getAreaDisplayName(activity) || '').toLowerCase();
        const areaType = String(activity.target_area_type || '').toLowerCase();
        const status = activity.is_active ? 'active' : 'inactive';
        const vlm = `${activity.vlm_done_count ?? 0}/${activity.vlm_count ?? 0}`;
        const mvlm = `${activity.mvlm_done_count ?? 0}/${activity.mvlm_count ?? 0}`;
        return (
          seasonName.includes(searchLower) ||
          areaName.includes(searchLower) ||
          areaType.includes(searchLower) ||
          status.includes(searchLower) ||
          vlm.includes(searchLower) ||
          mvlm.includes(searchLower)
        );
      });
    }

    // Location Type filter
    if (filters.locationType) {
      filtered = filtered.filter(activity =>
        activity.target_area_type &&
        activity.target_area_type.toLowerCase() === filters.locationType.toLowerCase()
      );
    }

    // Season filter
    if (filters.currentSeason) {
      filtered = filtered.filter(activity => 
        String(activity.season_id) === String(filters.currentSeason)
      );
    }

    // REMOVE all location-based filters so all activities are shown regardless of location filter selection

    return filtered;
  }, [preSeasonActivities, filters, District, Zones, Regions, Areas, Seasons]);
  console.log('filters:', filters);
  console.log('filteredActivities:', filteredActivities);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [filters]);

  const totalPages = Math.max(1, Math.ceil(filteredActivities.length / PAGE_SIZE));
  const pagedActivities = filteredActivities.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  
  // Stats data for the cards
  // const stats = [
  //   { label: "TOTAL TARGETS", value: "0", icon: <TrackChangesIcon />, bg: '#EEF4FF',  },
  //   { label: "ACTIVE TARGETS", value: "0", icon: <CheckCircleIcon />, bg: '#E6FBF4',  },
  //   { label: "INACTIVE TARGETS", value: "0", icon: <CancelIcon />, bg: '#FFF1F1',  },
  //   { label: "COMPLETION RATE", value: "0", icon: <DonutLargeIcon />, bg: '#F4F3FF',  },
  // ];

   const handleEditDealer = (activityId) => {
    console.log("Editing activity with ID:", activityId);
    if (!activityId) return;
    navigate('/masterData/addTarget', { state: { activityId, mode: 'edit' } });
  };

  const handleView = (activity) => {
    setViewRow(activity);
    setOpenViewDialog(true);
  };

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
    setViewRow(null);
  };
  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: { xs: 1, sm: 0 },
        px: isIpad ? 1 : 0,
        width: '100%',
        maxWidth: isIpad ? 800 : '100%',
        mx: 'auto',
        boxSizing: 'border-box',
        overflowX: 'hidden',
        overflowY: { md: 'hidden' },
        mt:{sm:8,md:0}
        
       
      }}
    >
      {/* Header */}
      <Box mb={4} sx={{ mt: isIpad ? 4 : 0 }}>
        <Typography variant="h6" fontWeight={600}   >
          Pre season Target Management
        </Typography>
      </Box>

      {/* Stats */}
      {/* <Box sx={{ width: '100%', mb: 3,mt:-2 }}>
        <Grid
          container
          spacing={{ xs: 1, sm: 1, md: 1 }}
          sx={{
            width: '100%',
            m: 0,
           
          }}
        >
          {stats.map((s) => (
                <Grid
                  item
                  size={{ xs: 6, sm: 3, md: 3 }}
                  key={s.label}
                  sx={{
                    p: 0,
                    display: 'flex',
                    justifyContent: 'stretch',
                  }}
                >
                  <Card sx={{ p: { xs: 1, sm: isIpad ? 1.5 : 2 }, borderRadius: 3, height: { xs: 70, sm: isIpad ? 60 : 50 }, width: '100%', mx: 0, display: 'flex', alignItems: 'center' }}>
                    <Box display="flex" alignItems="center" width="100%" justifyContent="space-between">
                      <Box display="flex" flexDirection="column" alignItems="flex-start" gap={0.5}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            fontSize: { xs: '0.80rem', sm: '0.80rem',md: '0.70rem' },
                             fontWeight: { xs: 600,sm: 600, md: 600 },
                            textAlign: 'left',
                            mb: 0,
                          }}
                        >
                          {s.label}
                        </Typography>
                        <Typography
                          variant="h5"
                          fontWeight={{ xs: 600,sm:600, md: 600 }}
                          sx={{
                            fontSize: { xs: '0.80rem', sm: isIpad ? '1.4rem' : '1.5rem' },
                            textAlign: 'left',
                            mb: 0,
                            fontWeight: { xs: 600,sm:600, md: 700 },
                          }}
                        >
                          {s.value}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          bgcolor: s.bg,
                          width: { xs: 36, sm: isIpad ? 44 : 45 },
                          height: { xs: 36, sm: isIpad ? 44 : 45 },
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 2,
                        }}
                      >
                        {s.icon}
                      </Box>
                    </Box>
                  </Card>
                </Grid>
          ))}
        </Grid>
      </Box> */}

      {/* Filters */}
      <Card sx={{ mr: { xs: 1 }, p: 2, borderRadius: 3, mb: 5 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item size={{ xs: 12, sm: 4, md: 2 }}>
            <TextField
              size="small"
              placeholder="Search ..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              slotProps={{
                input: {
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                },
              }}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                },
              }}
            />
          </Grid>

          <Grid item size={{ xs: 6, sm: 4, md: 2 }}>
            <Select
              size="small"
              value={filters.locationType}
              onChange={(e) => setFilters({ ...filters, locationType: e.target.value })}
              displayEmpty
              sx={{ width: '100%', borderRadius: 3 }}
            >
              <MenuItem value="">All Locations</MenuItem>
              <MenuItem value="District">District</MenuItem>
              <MenuItem value="Zone">Zone</MenuItem>
              <MenuItem value="Region">Region</MenuItem>
              <MenuItem value="Area">Area</MenuItem>
            </Select>
          </Grid>

          <Grid item size={{ xs: 6, sm: 4, md: 2 }}>
            <Select
              size="small"
              value={String(filters.district)}
              onChange={(e) => setFilters({ ...filters, district: e.target.value })}
              displayEmpty
              sx={{ width: '100%', borderRadius: 3 }}
            >
              <MenuItem value="">All Districts</MenuItem>
              {District.map((district) => (
                <MenuItem key={district.districtId} value={String(district.districtId)}>
                  {district.name}
                </MenuItem>
              ))}
            </Select>
          </Grid>

          <Grid item size={{ xs: 6, sm: 4, md: 2 }}>
            <Select
              size="small"
              value={filters.zone}
              onChange={(e) => setFilters({ ...filters, zone: e.target.value })}
              displayEmpty
              sx={{ width: '100%', borderRadius: 3 }}
            >
              <MenuItem value="">All Zones</MenuItem>
              {Zones.map((zone) => (
                <MenuItem key={zone.zoneId} value={zone.zoneId}>
                  {zone.zoneName}
                </MenuItem>
              ))}
            </Select>
          </Grid>

          <Grid item size={{ xs: 6, sm: 4, md: 2 }}>
            <Select
              size="small"
              value={filters.region}
              onChange={(e) => setFilters({ ...filters, region: e.target.value, area: '' })}
              displayEmpty
              sx={{ width: '100%', borderRadius: 3 }}
            >
              <MenuItem value="">All Regions</MenuItem>
              {filteredRegions.map((region) => (
                <MenuItem key={region.regionId} value={region.regionId}>
                  {region.regionName}
                </MenuItem>
              ))}
            </Select>
          </Grid>

          <Grid item size={{ xs: 6, sm: 4, md: 2 }}>
            <Select
              size="small"
              value={filters.area}
              onChange={(e) => setFilters({ ...filters, area: e.target.value })}
              displayEmpty
              sx={{ width: '100%', borderRadius: 3 }}
            >
              <MenuItem value="">All Areas</MenuItem>
              {filteredAreas.map((area) => (
                <MenuItem key={area.areaId} value={area.areaId}>
                  {area.areaName}
                </MenuItem>
              ))}
            </Select>
          </Grid>

          <Grid item size={{ xs: 6, sm: 4, md: 2 }}>
            <Select
              size="small"
              value={filters.currentSeason}
              onChange={(e) => setFilters({ ...filters, currentSeason: e.target.value })}
              displayEmpty
              sx={{ width: '100%', borderRadius: 3 }}
            >
              <MenuItem value="">All Seasons</MenuItem>
              {Seasons.map((season) => (
                <MenuItem key={season.seasonId} value={season.seasonId}>
                  {season.name}{season.year ? ` (${season.year})` : ''}
                </MenuItem>
              ))}
            </Select>
          </Grid>

          <Grid item size={{ xs: 12, sm: 4, md: 2 }}>
            <Button
              variant="contained"
              sx={{
                borderRadius: 2,
                whiteSpace: 'nowrap',
                '@media (min-width:1100px) and (max-width:1500px)': {
                  fontSize: '0.70rem',
                },
              }}
              fullWidth
              onClick={() => navigate("/masterData/addTarget")}
            >
              + Add New Target
            </Button>
          </Grid>
        </Grid>
      </Card>

      {/* Responsive Table/Card */}
      <Box sx={{  mb: 2 }}>
        {/* Header: visible only on sm and up, styled like Crop */}
        <Box
          display={{ xs: 'none', sm: 'grid' }}
          gridTemplateColumns={{ sm: '1fr 1fr 1fr 1fr 1fr 1fr 0.7fr 0.5fr' }}
          px={{ xs: 1, sm: 2 }}
          mb={{ xs: 1, sm: 1 }}
          sx={{ width: isIpad ? '97%' : undefined, mx: 'auto' }}
        >
          <Typography fontSize={{ sm: 12 }} color="#000000" fontWeight={600}>SEASON </Typography>
          <Typography fontSize={{ sm: 12 }} color="#000000" fontWeight={600}>AREA TYPE</Typography>
          <Typography fontSize={{ sm: 12 }} color="#000000" fontWeight={600}>AREA ID</Typography>
          <Typography fontSize={{ sm: 12 }} color="#000000" fontWeight={600}>VLM COUNT</Typography>
          <Typography fontSize={{ sm: 12 }} color="#000000" fontWeight={600}>MVLM COUNT</Typography>
          <Typography fontSize={{ sm: 12 }} color="#000000" fontWeight={600}>FARMER TARGET</Typography>
          <Typography fontSize={{ sm: 12 }} color="#000000" fontWeight={600}>STATUS</Typography>
          <Typography fontSize={{ sm: 12 }} color="#000000" fontWeight={600}>ACTIONS</Typography>
        </Box>

        {/* Rows styled like Crop */}
        {Array.isArray(filteredActivities) && filteredActivities.length > 0 ? (
          pagedActivities.map((activity) => (
            <Paper
              key={activity.id}
              elevation={0}
              sx={{
                mb: { xs: 1, sm: 2 },
                p: { xs: 1, sm: 2 },
                borderRadius: 3,
                display: { xs: 'block', sm: 'grid' },
                gridTemplateColumns: { sm: '1fr 1fr 1fr 1fr 1fr 1fr 0.7fr 0.5fr' },
                alignItems: 'center',
                border: '1px solid #E5E7EB',
                gap: { xs: 0, sm: 0 },
                width: isIpad ? '97%' : undefined,
                mx: 'auto',
                cursor: 'pointer',
                '&:hover': {
                  border: '2px solid #2563EB',
                  boxShadow: '0 4px 16px 0 rgba(37,99,235,0.15)',
                },
              }}
            >
              {/* Mobile layout */}
              <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                <Box mb={1} display="flex" alignItems="center" >
                  <Typography fontWeight={600} minWidth={100}>Season:</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                    <Typography variant="body2">{getSeasonDisplayName(activity)}</Typography>
                    <Chip
                      label={activity.is_active ? "Active" : "Inactive"}
                      size="small"
                      sx={{ width: 80, backgroundColor: activity.is_active ? '#10B981' : '#F87171', color: '#fff', ml: 'auto' }}
                    />
                  </Box>
                </Box>
                <Box mb={1} display="flex" alignItems="center" gap={0}>
                  <Typography fontWeight={600} minWidth={100}>Area Type:</Typography>
                  <Typography variant="body2">{activity.target_area_type}</Typography>
                </Box>
                <Box mb={1} display="flex" alignItems="center" gap={0}>
                  <Typography fontWeight={600} minWidth={100}>Area:</Typography>
                  <Typography variant="body2">{getAreaDisplayName(activity)}</Typography>
                </Box>
                <Box mb={1} display="flex" alignItems="center" gap={0}>
                  <Typography fontWeight={600} minWidth={100}>VLM Count:</Typography>
                  <Box>
                    <Typography variant="body2">{activity.vlm_done_count ?? 0}/{activity.vlm_count ?? 0}</Typography>
                  </Box>
                </Box>
                <Box mb={1} display="flex" alignItems="center" gap={0}>
                  <Typography fontWeight={600} minWidth={100}>MVLM Count:</Typography>
                  <Box>
                    <Typography variant="body2">{activity.mvlm_done_count ?? 0}/{activity.mvlm_count ?? 0}</Typography>
                  </Box>
                </Box>
                <Box mb={1} display="flex" alignItems="center" gap={0}>
                  <Typography fontWeight={600} minWidth={100}>FARMER TARGET:</Typography>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>{activity.farmers_attended || 0}/{activity.farmers_required || 0}</Typography>
                    <LinearProgress
                      variant="determinate"
                      value={activity.farmers_required > 0 ? Math.min((activity.farmers_attended || 0) / activity.farmers_required * 100, 100) : 0}
                      sx={{
                        height: 8,
                        borderRadius: 1,
                        backgroundColor: '#E5E7EB',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: getProgressBarColor(activity.farmers_required > 0 ? (activity.farmers_attended || 0) / activity.farmers_required * 100 : 0),
                          borderRadius: 1,
                        },
                      }}
                    />
                  </Box>
                </Box>
                <Box display="flex" alignItems="center" gap={0}>
                  <Typography fontSize={11} color="#000000" minWidth={100}></Typography>
                  <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                    <IconButton size="small" onClick={() => handleView(activity)}>
                      <Visibility fontSize="small" sx={{ color: '#036144', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
                    </IconButton>
                    <IconButton size="small">
                      <EditOutlined fontSize="small" sx={{ color: '#2563EB', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
              {/* Desktop layout */}
              <Typography display={{ xs: 'none', sm: 'block' }} variant="body2">{getSeasonDisplayName(activity)}</Typography>
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                <Typography variant="body2">{activity.target_area_type}</Typography>
              </Box>
              <Typography display={{ xs: 'none', sm: 'block' }} variant="body2">{getAreaDisplayName(activity)}</Typography>
              <Box display={{ xs: 'none', sm: 'flex' }} flexDirection="column" gap={0.5} sx={{ ml: { lg: 0 } }}>
                <Typography variant="body2">{activity.vlm_done_count ?? 0}/{activity.vlm_count ?? 0}</Typography>
              </Box>
              <Box display={{ xs: 'none', sm: 'flex' }} flexDirection="column" gap={0.5} sx={{ ml: { lg: 0 } }}>
                <Typography variant="body2">{activity.mvlm_done_count ?? 0}/{activity.mvlm_count ?? 0}</Typography>
              </Box>
              <Box display={{ xs: 'none', sm: 'flex' }} flexDirection="column" gap={0.5} sx={{ ml: { lg: 0 }, pr: 1 }}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>{activity.farmers_attended || 0}/{activity.farmers_required || 0}</Typography>
                <LinearProgress
                  variant="determinate"
                  value={activity.farmers_required > 0 ? Math.min((activity.farmers_attended || 0) / activity.farmers_required * 100, 100) : 0}
                  sx={{
                    height: 8,
                    borderRadius: 1,
                    backgroundColor: '#E5E7EB',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: getProgressBarColor(activity.farmers_required > 0 ? (activity.farmers_attended || 0) / activity.farmers_required * 100 : 0),
                      borderRadius: 1,
                    },
                  }}
                />
              </Box>
              <Box sx={{ display: { xs: 'none', sm: 'flex' }, justifyContent: 'center', alignItems: 'center' }}>
                <Chip
                  label={activity.is_active ? "Active" : "Inactive"}
                  size="small"
                  sx={{ width: { sm: 80 }, backgroundColor: activity.is_active ? '#D1FAE5' : '#FECACA', color: activity.is_active ? '#047857' : '#B91C1C', fontSize: 12, fontWeight: 600 }}
                />
              </Box>
              <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 1, ml: 0 }}>
                <IconButton size="small" onClick={() => handleView(activity)}>
                  <Visibility fontSize="small" sx={{ color: '#036144', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
                </IconButton>
                <IconButton size="small" onClick={() => handleEditDealer(activity.id)}>
                  <EditOutlined fontSize="small" sx={{ color: '#2563EB', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
                </IconButton>
              </Box>
            </Paper>
          ))
        ) : (
          <Typography align="center" color="text.secondary" sx={{ mt: 2 }}>
            No pre-season targets found.
          </Typography>
        )}
        {filteredActivities.length > 0 && totalPages > 1 && (
          <Box display="flex" alignItems="center" justifyContent="space-between" mt={2} flexWrap="wrap" gap={1}>
            <Typography variant="body2" >
              Showing {Math.min((page - 1) * PAGE_SIZE + 1, filteredActivities.length)} to {Math.min(page * PAGE_SIZE, filteredActivities.length)} of {filteredActivities.length} entries
            </Typography>
            <Pagination count={totalPages} page={page} onChange={(_, val) => setPage(val)} color="primary" />
          </Box>
        )}
      </Box>

      {/* Pre-Activity Details View Dialog */}
      <PreActivityDetailsView 
        open={openViewDialog} 
        onClose={handleCloseViewDialog} 
        row={viewRow} 
      />
    </Box>
  );
}

